/**
 * Whether the listed runs are a narrowed subset of the project by any
 * narrowing criteria like filters, search, or hidden runs.
 */
export function isRunListNarrowed({
  activeFilterCount,
  hasSearchQuery,
  onlyShowSelected,
}: {
  activeFilterCount: number;
  hasSearchQuery: boolean;
  onlyShowSelected: boolean;
}): boolean {
  return activeFilterCount > 0 || hasSearchQuery || onlyShowSelected;
}

/**
 * Builds the count tag label.
 *
 * Priority:
 * temp selection >
 * "N of K listed" (listed is a strict subset) >
 * "N listed".
 *
 * When countCap is set, selected/listed display counts are capped and
 * selectedCount is treated as the uncapped total.
 */
export function getRunsCountLabel({
  isTempSelectionActive,
  tempSelectedCount,
  selectedCount,
  listedCount,
  totalRunsCount,
  hasTotalRunsCountError,
  totalRunsCountSkipped,
  countCap,
  isCountStale = false,
}: {
  isTempSelectionActive: boolean;
  tempSelectedCount: number;
  selectedCount: number;
  listedCount: number;
  totalRunsCount: number;
  hasTotalRunsCountError: boolean;
  totalRunsCountSkipped: boolean;
  countCap?: number;
  // On large projects where runs have been logged since the count loaded, the
  // count may undercount, so we append "+" to the displayed number (e.g.
  // "500+ listed"). Not applied to an exact temp selection.
  isCountStale?: boolean;
}): string {
  if (isTempSelectionActive) {
    return `${tempSelectedCount.toLocaleString()} selected`;
  }

  const displayListedCount =
    countCap == null ? listedCount : Math.min(listedCount, countCap);
  const displayTotalRunsCount =
    countCap == null ? totalRunsCount : selectedCount;
  const displayHasTotalRunsCountError =
    countCap == null && hasTotalRunsCountError;
  const displayTotalRunsCountSkipped =
    countCap == null && totalRunsCountSkipped;

  // Append "+" whenever new runs have likely been logged since load, including
  // when the count is capped by the grouping aggregation limit: the underlying
  // total can still grow, so the group warning should still get a "+". An exact
  // temp selection is handled above and never gets a "+".
  const fmt = (n: number): string =>
    isCountStale ? `${n.toLocaleString()}+` : n.toLocaleString();

  // listed count is a subset of the total count, so show "of K" where
  // K is the total count in project.
  const showOfTotal =
    !displayHasTotalRunsCountError &&
    !displayTotalRunsCountSkipped &&
    displayTotalRunsCount > 0 &&
    displayListedCount < displayTotalRunsCount;
  if (showOfTotal) {
    return `${displayListedCount.toLocaleString()} of ${fmt(displayTotalRunsCount)} listed`;
  }

  return `${fmt(displayListedCount)} listed`;
}
